import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import StatusChip from "../StatusChip";
import { projectApi, roleLabel, permissionSummary, errText } from "../../lib/projectAccess";

const STATUS = { active: "connected", removed: "not_configured", disabled: "error" };

export default function TeamMemberRow({ projectId, member: m, canRemove, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const tid = `project-team-member-${m.user_id}`;
  const perms = permissionSummary(m.permissions);

  const remove = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await projectApi.remove(projectId, m.assignment_id, reason.trim());
      toast.success("Removed from the project");
      setOpen(false);
      onRemoved();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="bg-white border border-erf-borderCream rounded-md p-3 sm:p-4" data-testid={tid}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-nav text-sm font-semibold text-erf-dark break-words" data-testid={`${tid}-name`}>
            {m.display_name || "Unnamed user"}
          </div>
          <div className="text-xs text-erf-dark/70 mt-0.5" data-testid={`${tid}-role`}>
            {roleLabel(m.role_on_project)}{m.role_key ? ` · ${m.role_key.replace(/_/g, " ").toLowerCase()}` : ""}
          </div>
          <div className="text-[11px] font-mono text-erf-dark/60 mt-1" data-testid={`${tid}-dates`}>
            Assigned {(m.assigned_at || "").slice(0, 10) || "—"}
            {m.removed_at ? ` · removed ${m.removed_at.slice(0, 10)}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip status={STATUS[m.status] || "phase_n"} label={m.status} testId={`${tid}-status`} />
          {canRemove && !open && (
            <Button type="button" variant="outline" className="min-h-[44px] min-w-[44px] border-erf-borderCream"
              onClick={() => setOpen(true)} data-testid={`${tid}-remove-btn`}>Remove</Button>
          )}
        </div>
      </div>
      {perms && (
        <p className="mt-2 text-[11px] font-mono text-erf-dark/70 break-words" data-testid={`${tid}-permissions`}>
          Can: {perms}
        </p>
      )}
      {open && (
        <form onSubmit={remove} className="mt-3 grid gap-2 sm:flex sm:items-end" data-testid={`${tid}-remove-form`}>
          <label className="grid gap-1 text-xs font-nav text-erf-dark/80 sm:flex-1" htmlFor={`${tid}-reason`}>
            Reason (at least 10 characters, recorded in the audit log)
            <input id={`${tid}-reason`} value={reason} onChange={(e) => setReason(e.target.value)}
              className="min-h-[44px] rounded border border-erf-borderCream px-3 text-sm focus:outline-none focus:ring-2 focus:ring-erf-gold"
              data-testid={`${tid}-remove-reason`} />
          </label>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || reason.trim().length < 10} className="min-h-[44px] bg-erf-dark text-white"
              data-testid={`${tid}-remove-confirm`}>Remove</Button>
            <Button type="button" variant="outline" className="min-h-[44px]" onClick={() => setOpen(false)}
              data-testid={`${tid}-remove-cancel`}>Cancel</Button>
          </div>
        </form>
      )}
    </li>
  );
}
